import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { checkPermission } from 'utils/utils';
import { Tabs } from 'antd';
import { AppDispatch } from 'redux/store';
import { getExpenseGroupsAction } from 'redux/action/expenseGroupAction';
import { updateCurrentTab } from 'redux/slice/transactionSlice';
import { clearTemporaryConfig } from 'redux/slice/expenseGroupAllocationSlice';
import { clearOneOffConfigurations } from 'redux/slice/oneOffSlice';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import styles from './index.module.scss';
import ExpenseTransactionMain from 'components/ExpenseAllocation/ExpenseTransactionMain';
import ExpenseAllocationMain from 'components/ExpenseAllocation/ExpenseAllocationMain';
import ExpenseJournalMain from 'components/ExpenseAllocation/ExpenseJournalMain';
import { clearExpenseGroups } from 'redux/slice/expenseGroupSlice';
// Extend dayjs with timezone plugins
dayjs.extend(utc);
dayjs.extend(timezone);

const TIMEZONE = process.env.REACT_APP_TIMEZONE || 'America/New_York';

export default function ExpenseAllocationPage() {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [searchParams] = useSearchParams();

    const selectedCompanyPermission = useSelector(
        (state: any) => state?.companies?.selectedCompanyDetails?.role?.permissions
    );
    const selectedCompanyDetails = useSelector(
        (state: any) => state?.companies?.selectedCompanyDetails
    );
    // Get current tab from Redux instead of useState
    const { currentTab } = useSelector((state: any) => state.transactions);

    // Global expense group state management
    const { data: expenseGroups, fistTimeFetchLoading: expenseGroupsLoading } = useSelector((state: any) => state.expenseGroups);
    const [selectedExpenseGroup, setSelectedExpenseGroup] = useState<string>('all');
    const [expenseGroupOptions, setExpenseGroupOptions] = useState<Array<{ value: string; label: string }>>([]);

    // Shared date range state for all tabs
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(() => {
        // Default to current month range (timezone-aware)
        const today = dayjs().tz(TIMEZONE);
        const firstDayOfCurrentMonth = today.startOf('month');
        const lastDayOfCurrentMonth = today.endOf('month');
        return [firstDayOfCurrentMonth, lastDayOfCurrentMonth];
    });

    // Check if user has any expense allocation permission (parent module check)
    const isExpenseTransactionPermission = checkPermission(selectedCompanyPermission, {
        permissionName: 'Expense Transactions',
        permission: ['view'],
    });
    const isExpenseJournalPermission = checkPermission(selectedCompanyPermission, {
        permissionName: 'Expense Journal',
        permission: ['view'],
    });
    const isExpenseGroupsPermission = checkPermission(selectedCompanyPermission, {
        permissionName: 'Expense Groups',
        permission: ['view'],
    });

    const isViewExpenseAllocationPermission = isExpenseTransactionPermission ||
        isExpenseJournalPermission;

    // Expense Allocation tab requires both Expense Transactions and Expense Groups view permissions
    const isExpenseAllocationTabPermission = isExpenseTransactionPermission && isExpenseGroupsPermission;

    // Transform expense groups data to dropdown format
    useEffect(() => {
        const allOption = { value: 'all', label: 'All Transactions' };
        const transformedGroups = (expenseGroups || []).map((group: any) => ({
            value: group.id,
            label: group.name
        }));
        setExpenseGroupOptions([allOption, ...transformedGroups]);
    }, [expenseGroups]);

    // Handle expense group selection
    const handleExpenseGroupChange = (expenseGroupId: string) => {
        const prevExpenseGroup = selectedExpenseGroup;
        setSelectedExpenseGroup(expenseGroupId);

        // Navigate to Tab 1 when mode changes (to/from "All Transactions")
        const isOneOffMode = expenseGroupId === 'all';
        const wasOneOffMode = prevExpenseGroup === 'all';
        if (isOneOffMode !== wasOneOffMode) {
            dispatch(updateCurrentTab('1'));
            dispatch(clearOneOffConfigurations());
        }

    };

    // Fetch expense groups on component mount
    useEffect(() => {
        dispatch(getExpenseGroupsAction({ page: 1, limit: 150 }));
    }, [dispatch]);

    // Handle company changes - refetch expense groups when company changes
    useEffect(() => {
        if (selectedCompanyDetails) {
            // Reset expense group selection when company changes
            setSelectedExpenseGroup('all');

            // Clear expense groups when company changes
            dispatch(clearExpenseGroups());
            // Clear temporary config when company changes
            // Temporary config is tied to a specific company's expense group
            dispatch(clearTemporaryConfig());

            // Clear one-off configurations on company change
            dispatch(clearOneOffConfigurations());

            // Refetch expense groups for the new company
            dispatch(getExpenseGroupsAction({ page: 1, limit: 150 }));
        }
    }, [selectedCompanyDetails, dispatch]);

    useEffect(() => {
        document.title = 'CostAllocation Pro - Expense Allocations';
    }, []);

    useEffect(() => {
        if (!isViewExpenseAllocationPermission && selectedCompanyPermission) {
            navigate('/unauthorized');
        }
    }, [isViewExpenseAllocationPermission, selectedCompanyPermission]);

    // Auto-select the first available tab based on permissions
    useEffect(() => {
        if (!isExpenseTransactionPermission && isExpenseJournalPermission) {
            dispatch(updateCurrentTab('3')); // Journal Entries tab
        }
    }, [isExpenseTransactionPermission, isExpenseJournalPermission, dispatch]);

    // Handle tab parameter from deep links
    useEffect(() => {
        const tabParam = searchParams.get('tab');
        const fromDateParam = searchParams.get('fromDate');
        const toDateParam = searchParams.get('toDate');

        if (tabParam === '1') {
            dispatch(updateCurrentTab('1'));

            // Handle date range parameters if they exist
            if (fromDateParam && toDateParam) {
                const fromDate = dayjs(fromDateParam);
                const toDate = dayjs(toDateParam);

                // Validate dates and set date range
                if (fromDate.isValid() && toDate.isValid()) {
                    setDateRange([fromDate, toDate]);
                }
            }

            // Clear the parameters from URL after processing
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.delete('tab');
            newSearchParams.delete('fromDate');
            newSearchParams.delete('toDate');
            const newUrl = newSearchParams.toString()
                ? `/expense-allocations?${newSearchParams.toString()}`
                : '/expense-allocations';
            navigate(newUrl, { replace: true });
        }
    }, [searchParams, navigate]);

    // Listen for notification date range updates
    useEffect(() => {
        const handleNotificationDateRangeUpdate = (event: CustomEvent) => {
            const { dateRange: newDateRange, notificationType } = event.detail;

            if (newDateRange && Array.isArray(newDateRange) && newDateRange.length === 2) {
                // Validate that the dates are dayjs objects
                const [startDate, endDate] = newDateRange;
                if (dayjs.isDayjs(startDate) && dayjs.isDayjs(endDate)) {
                    // Update the date range state
                    setDateRange([startDate, endDate] as [dayjs.Dayjs, dayjs.Dayjs]);

                    // Dispatch custom event to trigger data fetch in ExpenseTransactionMain
                    const fetchEvent = new CustomEvent('triggerDataFetch', {
                        detail: {
                            dateRange: [startDate, endDate],
                            notificationType
                        }
                    });
                    window.dispatchEvent(fetchEvent);
                }
            }
        };

        // Add event listener
        window.addEventListener('notificationDateRangeUpdate', handleNotificationDateRangeUpdate as EventListener);

        // Cleanup event listener on component unmount
        return () => {
            window.removeEventListener('notificationDateRangeUpdate', handleNotificationDateRangeUpdate as EventListener);
        };
    }, []);

    const items = [
        ...(isExpenseTransactionPermission ? [{
            key: '1',
            label: 'Expense Transaction',
            children: (
                <ExpenseTransactionMain
                    propSelectedExpenseGroup={selectedExpenseGroup}
                    propOnExpenseGroupChange={handleExpenseGroupChange}
                    propExpenseGroupOptions={expenseGroupOptions}
                    propExpenseGroupsLoading={expenseGroupsLoading}
                    propDateRange={dateRange}
                    propSetDateRange={setDateRange}
                />
            ),
        }] : []),
        ...(isExpenseAllocationTabPermission ? [{
            key: '2',
            label: 'Expense Allocation',
            children: (
                <ExpenseAllocationMain
                    selectedExpenseGroup={selectedExpenseGroup}
                    onExpenseGroupChange={handleExpenseGroupChange}
                    expenseGroupOptions={expenseGroupOptions}
                    expenseGroupsLoading={expenseGroupsLoading}
                    dateRange={dateRange}
                    onSwitchToJournalEntries={() => dispatch(updateCurrentTab('3'))}
                />
            ),
        }] : []),
        ...(isExpenseJournalPermission ? [{
            key: '3',
            label: 'Expense Journal Entries',
            children: (
                <ExpenseJournalMain
                    selectedExpenseGroup={selectedExpenseGroup}
                    onExpenseGroupChange={handleExpenseGroupChange}
                    expenseGroupOptions={expenseGroupOptions}
                    expenseGroupsLoading={expenseGroupsLoading}
                />
            ),
        }] : []),
    ];

    return (
        <>
            <div className={styles['expense-allocation-container']}>
                <div className={styles['expense-allocation-container__table']}>
                    <div className={styles['expense-allocation-main']}>
                        <Tabs
                            defaultActiveKey="1"
                            items={items}
                            activeKey={currentTab}
                            key={currentTab}
                            tabBarStyle={{
                                paddingLeft: '20px',
                                fontSize: '18px',

                            }}
                            onChange={(e) => {
                                dispatch(updateCurrentTab(e));
                            }}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}
